import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { ChartConfig } from '@goaltree/ui/components/chart'
import { ChartContainer } from '@goaltree/ui/components/chart'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { ChevronRight, TrendingUp } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Area, AreaChart, ReferenceLine, XAxis, YAxis } from 'recharts'

import { goalSlug } from '@/lib/utils'

// Remember the last teaser across remounts so re-focusing Today renders it
// immediately (like the streak) instead of the query reloading from scratch and
// popping the row in a beat later.
let cachedTeaser: FunctionReturnType<typeof api.projection.getTrajectoryTeaser> = null

// One-line Today teaser: the nearest projected win, deep-linking to its goal.
export function TrajectoryTeaser({ className }: { className?: string }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const teaser = useQuery(api.projection.getTrajectoryTeaser, {})
  useEffect(() => {
    if (teaser !== undefined) cachedTeaser = teaser
  }, [teaser])
  // Bridge only the loading gap: while undefined show the cached value; once
  // loaded (object or a real null) honour the fresh result.
  const shown = teaser === undefined ? cachedTeaser : teaser
  if (!shown) return null

  const date = new Date(shown.projectedFinishAt).toLocaleDateString(i18n.language, {
    month: 'short',
    day: 'numeric',
  })
  return (
    <button
      type="button"
      onClick={() =>
        navigate({
          to: '/goals/$goalId',
          params: { goalId: goalSlug(shown.title, shown.goalId as string) },
        })
      }
      className={cn(
        'flex w-full items-center gap-2.5 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-left transition-colors hover:bg-primary/10',
        className,
      )}
    >
      <TrendingUp className="h-4 w-4 shrink-0 text-primary-text" />
      <span className="min-w-0 flex-1 truncate text-sm">
        {t('trajectory.teaser', { goal: shown.title, date })}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  )
}

// "The Compound View" — reframes daily completions as proof of where the goal is
// headed: an honest linear projection of the finish date at the recent pace,
// with a cumulative-progress curve that runs actual → projected.
export function TrajectoryPanel({
  goalId,
  className,
}: {
  goalId: Id<'nodes'>
  className?: string
}) {
  const { t, i18n } = useTranslation()
  const data = useQuery(api.projection.getGoalProjection, { goalId })

  if (!data || data.total === 0 || data.remaining === 0) return null

  const fmtDate = (ts: number) =>
    new Date(ts).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

  // Not enough signal yet — invite them to keep going rather than fake a curve.
  // Hints use balanced spacing (not the chart panel's pull-up className).
  if (!data.hasEnoughData) {
    return <p className="mb-8 text-xs text-muted-foreground">{t('trajectory.gathering')}</p>
  }

  // Enough data but stalled (no recent completions → no honest ETA).
  if (!data.projectedFinishAt) {
    return (
      <p className="mb-8 text-xs text-muted-foreground">
        {t('trajectory.stalled', { remaining: data.remaining })}
      </p>
    )
  }

  const chartConfig = {
    done: { label: t('trajectory.completed'), color: 'var(--primary)' },
    projected: { label: t('trajectory.projected'), color: 'var(--muted-foreground)' },
  } satisfies ChartConfig

  return (
    <div className={cn(className)}>
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary-text">
          <TrendingUp className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight">
            {t('trajectory.onPaceBy', { date: fmtDate(data.projectedFinishAt) })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('trajectory.pace', {
              rate: Math.max(1, Math.round(data.velocityPerWeek)),
              remaining: data.remaining,
            })}
          </p>
        </div>
      </div>

      <ChartContainer config={chartConfig} className="mt-3 h-36 w-full">
        <AreaChart data={data.series} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="traj-done" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-done)" stopOpacity={0.35} />
              <stop offset="100%" stopColor="var(--color-done)" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="t"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(v) => new Date(v).toLocaleDateString(i18n.language, { month: 'short' })}
            tickLine={false}
            axisLine={false}
            fontSize={10}
            minTickGap={28}
          />
          <YAxis hide domain={[0, data.total]} />
          <ReferenceLine
            x={Date.now()}
            stroke="var(--border)"
            strokeDasharray="3 3"
            label={{
              value: t('trajectory.now'),
              position: 'insideTopRight',
              fontSize: 9,
              fill: 'var(--muted-foreground)',
            }}
          />
          <Area
            dataKey="projected"
            type="monotone"
            stroke="var(--color-projected)"
            strokeWidth={2}
            strokeDasharray="5 4"
            fill="none"
            connectNulls
            isAnimationActive={false}
            dot={false}
          />
          <Area
            dataKey="done"
            type="monotone"
            stroke="var(--color-done)"
            strokeWidth={2}
            fill="url(#traj-done)"
            connectNulls
            isAnimationActive={false}
            dot={false}
          />
        </AreaChart>
      </ChartContainer>
    </div>
  )
}
